<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('evaluationDetail') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId" >
                        {{ assetTypeName || $t('assetType') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId + '/parameter/' + this.$route.params.parameterId" >
                        {{ parameterName || $t('parameter') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ data.name }}</li>
            </ol>
        </nav>
       <div class="row mb-3">
            <div class="col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
            <div class="col-md-8">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description">
            </div>
        </div>
        <div class="mb-3">
            <h4>{{ $t('evaluationFilter') }}</h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('name') }}</th>
                        <th scope="col">{{ $t('sign') }}</th>
                        <th scope="col">{{ $t('number') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(evaluationFilter, i) in data.parameterEvaluationFilter" class="align-middle" :key="i">
                        <th scope="row"><i class="fa fa-remove" @click="removeEvaluationFilter(i)"></i></th>
                        <td>
                            <select :id="'name_' + o"  class="form-select" v-model="evaluationFilter.name">
                                <option disabled>{{ $t('select') }}</option>
                                <option v-for="(datasetHeader, i) in datasetHeaders" :disabled="datasetHeader.disabled" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                            </select>
                        </td>
                        <td>
                            <select v-model="evaluationFilter.sign" class="form-select" :id="'sign_' + o"> 
                                <option value=""></option>
                                <option value="=">=</option>
                                <option value=">">&gt;</option>
                                <option value="<">&lt;</option>
                                <option value=">=">&gt;=</option>
                                <option value="<=">&lt;=</option>
                            </select>
                        </td>
                        <td>
                            <input v-model="evaluationFilter.number" type="number" class="form-control" :id="'number_number' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block" @click="addEvaluationFilter"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="mb-3">
            <h4>{{ $t('classTransformation') }}</h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col">{{ $t('className') }}</th>
                        <th scope="col">{{ $t('startParameter') }}</th>
                        <th scope="col">{{ $t('endParameter') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(parameterClass, i) in parameterClasses" class="align-middle"  :key="i">
                        <td>
                            <input v-model="parameterClass.name" disabled class="form-control" :id="'parameterClass.name_' + i">
                        </td>
                        <td>
                            <input v-model="parameterClass.startParameter" :disabled="i > 0" type="number" class="form-control" :id="'startParameter_' + i">
                        </td>
                        <td>
                            <input v-model="parameterClass.endParameter" @input="updateParameterEvaluationTransformationStart(parameterClass.endParameter, i)"  type="number" class="form-control" :id="'endParameter_' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="d-grid gap-2 d-md-flex ">
            <template v-if="isUserAdmin">
                <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
            </template>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}/parameter/${this.$route.params.parameterId}` })">{{ $t('back') }}</button>
        </div>
        <ParameterDeleteModal v-if="showParameterDeleteModal" @reload="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })" @closeDeleteDialog="showParameterDeleteModal = false"/>
    </div>
</template>

<script>
import ParameterApi from "../../api/server/parameter";
import DatasetApi from "../../api/server/dataset";
import AssetTypeApi from "../../api/server/assetType";
import ParameterDeleteModal from '../parameters/ParameterDeleteModal.vue';
import ModelingApi from "../../api/server/modeling";
import { CheckIfUserIsEditor } from '../../authCache';

export default {
    name: "parameterdetail",
    watch: {
        $route: "requestData",
    },
    components: {
        ParameterDeleteModal,
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.pending = true;
            this.isUserAdmin = await CheckIfUserIsEditor();

            var parameter = await ParameterApi.parameterDetail(this.$route.params.id, this.$route.params.parameterId);
            this.parameterName = parameter.name;

            var assetType = await AssetTypeApi.assetTypeDetail(this.$route.params.id, this.$route.params.assetTypeId);
            this.assetTypeName = assetType.name;

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            var dataset = await DatasetApi.datasetDetail(this.$route.params.id, assetType.datasetId);
            this.datasetHeaders = JSON.parse(dataset.content).headers;

            this.data = await ParameterApi.evaluationDetail(this.$route.params.id,this.$route.params.parameterId,this.$route.params.evaluationId);
            this.parameterClasses = parameter.parameterClasses.map((cls) => {
                const t = this.data.parameterEvaluationTransformation.find((tr) => tr.parameterClassId === cls.id);
                return { 
                    // ...cls,
                    name: cls.name,
                    parameterClassId: cls.id,
                    startParameter: t?.startParameter ?? 0,
                    endParameter: t?.endParameter ?? 0
                };
            });

            this.pending = false;
        },
        async onSubmit() {
            if(this.data.name.length > 0) {
                this.data.parameterEvaluationTransformation = this.parameterClasses;
                await ParameterApi.evaluationUpdate(this.$route.params.id,this.$route.params.parameterId, this.data);

                if(!this.isFormValid())
                    this.showWarning();
                else 
                    this.showSuccess();
            } else {
                this.showEmptyNameError();
            }
        },
        isFormValid() {
            for(var i in this.data.parameterEvaluationFilter) {
                var attr = this.data.parameterEvaluationFilter[i].name;
                if(this.datasetHeaders.findIndex(d => d.name == attr) < 0 && attr != "")
                    return false;
            }

            return true
        },
        showDeleteParameter() {
            this.parameterToDeleteId = this.data.id;
            this.showParameterDeleteModal = true;
        },
         updateParameterEvaluationTransformationStart(value, i) {
            var range = this.parameterClasses[i+1];
            if(range) {
                range.startParameter = value;
            }
        },
        getMinStartParameterEvaluationTransformation() {
            var minStartParameter = 0;
            if(this.parameterClasses.length > 0)
            minStartParameter = this.parameterClasses[this.parameterClasses.length -1].endParameter;

            return minStartParameter;
        }, 
        removeEvaluationFilter(rowIndex) {
            this.data.parameterEvaluationFilter.splice(rowIndex, 1);
        },
        addEvaluationFilter() {
            this.data.parameterEvaluationFilter.push({  
                name: "",
                sign: "",
                number: 0
            })
        },
    },
    notifications: {
        showSuccess: { 
            title: 'success',
            message: 'evaluationUpdated',
            type: 'success'
        },
        showWarning: {
            title: 'warning',
            message: 'warningAttributeRemoved',
            type: 'warn'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            costFactors: [],
            missingDatasetHeaders: [],
            datasetHeaders: [],
            data: {
                name: "",
                id: ""
            },
            showParameterDeleteModal: false,
            parameterToDeleteId: 0,
            modelingName: null,
            assetTypeName: null,
            parameterName: null,
            pending: true,
            isUserAdmin: false,
            parameterClasses: []
        }
    }
};
</script>